/**
 * @file   src\components\AgentDashboard\ViewClientInventory.js
 * @brief  This file is responsible for handling Inventories of each client
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState } from "react";
import {
  removeDecimalZeros,
  replaceComma,
  truncate,
} from "../../helpers/common";
import { checkValidImage } from "../../helpers/inventory";
import HouseTagIcon from "../../images/house-tag-icon.svg";
import LotIcon from "../../images/lot-icon.svg";
import BedIcon from "../../images/bed-icon.svg";
import BathIcon from "../../images/bath-icon.svg";
import SqftIcon from "../../images/sqft-icon.svg";
import WantAdMoreIcon from "../../images/want-ad-more-icon.svg";
import noImage from "../../images/no-image-placeholder.png";
import Dropdown from "react-bootstrap/Dropdown";
import {
  INVENTORY_LTR,
  INVENTORY_LTR_STR,
  INVENTORY_SELL,
  INVENTORY_SELL_LTR,
  INVENTORY_SELL_STR,
  INVENTORY_STR,
  IS_LAND,
} from "../../constants/common";
import { checkInventoryType } from "../../helpers/inventory";
import MarkAsSoldConfirmModal from "../../components/Inventory/MarkAsSoldConfirmModal";
import HelpFeedbackModal from "../../components/Inventory/HelpFeedbackModal";
import { inventoryMarkAsSold } from "../../services/inventoryService";
import { toast } from "react-toastify";

const ViewClientInventory = (props) => {
  const {
    inventory,
    MarkAsSoldSuccess,
    onEdit,
    onInventoryDelete,
    handleInventoryItemClick,
  } = props;

  const [markAsSoldModalShow, setMarkAsSoldModal] = useState(false);
  const handleMarkAsSoldModal = (status) => setMarkAsSoldModal(status);
  const [showGitchaHelpFeedbackModal, setShowGitchaHelpFeedbackModal] =
    useState(false);
  const GitchaHelpFeedbackModalShow = () =>
    setShowGitchaHelpFeedbackModal(true);
  const GitchaHelpFeedbackModalClose = () =>
    setShowGitchaHelpFeedbackModal(false);
  const [markAsSoldStatus, setMarkAsSoldStatus] = useState(false);

  const invType = checkInventoryType(_.get(inventory, "proprtyPurpose", ""));
  const bedRoom = removeDecimalZeros(_.get(inventory, "Bedrooms", ""));
  const bathRoom = removeDecimalZeros(_.get(inventory, "Bathrooms", ""));
  const sqFt = removeDecimalZeros(_.get(inventory, "Sqft", ""));
  const acreage = removeDecimalZeros(_.get(inventory, "TotalAcre", ""));
  let lotSizeUnit = _.get(inventory, "Acre_Sqft", "");
  if (lotSizeUnit && lotSizeUnit !== 0) {
    lotSizeUnit = lotSizeUnit === 1 ? " Acres" : " Sqft";
  }
  const isDraftItem = _.get(inventory, "IsSavedDraft", "");
  const isSold = _.get(inventory, "IsSold", "");
  const inventoryId = _.get(inventory, "Inv_Id", "");
  const showMarkAsSoldBtn = !isDraftItem && !isSold;
  const photos = _.get(inventory, "CoverPhotos", []);
  const firstPhoto = checkValidImage({ photos: photos, index: 0 });
  const secondPhoto = checkValidImage({ photos: photos, index: 1 });
  const thirdPhoto = checkValidImage({ photos: photos, index: 2 });
  let titleText = "";
  switch (invType) {
    case INVENTORY_SELL:
      titleText = " selling";
      break;
    case INVENTORY_LTR:
      titleText = " renting";
      break;
    case INVENTORY_STR:
      titleText = " renting";
      break;
    case INVENTORY_LTR_STR:
      titleText = " renting";
      break;
    case INVENTORY_SELL_LTR:
      titleText = " selling, renting";
      break;
    case INVENTORY_SELL_STR:
      titleText = " selling, renting";
      break;
    default:
      titleText = " selling, renting";
      break;
  }

  const onMarkAsSold = () => {
    handleMarkAsSoldModal(true);
  };

  /**
   * To handle mark as sold
   */
  const handleInventoryMarkAsSold = async (
    inventoryId,
    didGitchHelped,
    salesPrice
  ) => {
    const newPrice = salesPrice?.replace("$", "");
    if (inventoryId) {
      const payload = {
        inventoryId: inventoryId,
        didGitchHelped: didGitchHelped === true ? 1 : 0,
        salesPrice: newPrice ? replaceComma(newPrice) : null,
      };
      await inventoryMarkAsSold(payload)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            toast.success("Your inventory has been successfully mark as sold.");
            GitchaHelpFeedbackModalClose();
            setMarkAsSoldStatus(!markAsSoldStatus);
            MarkAsSoldSuccess();
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  return (
    <div className="col-md-4 mb-4">
      <a
        href={void 0}
        role={!isDraftItem ? "button" : ""}
        className="txt-no-underline"
        onClick={() =>
          !isDraftItem ? handleInventoryItemClick(inventoryId) : ""
        }
      >
        <div className="my-wantad-listing mb-4 mb-md-0">
          <div className="inventory-banner-small position-relative">
            <div className="modal-property-tile tile-loation">
              {_.get(inventory, "PropertyTypeId", "") === IS_LAND ? (
                <img src={LotIcon} />
              ) : (
                <img src={HouseTagIcon} />
              )}
            </div>

            {photos.length === 0 && (
              <div className="banner-row">
                <div className="banner-column-full w-100 h-166">
                  <div className="full-col-img-wrap h-100 placeholder-wrap-bg brdr-8">
                    <img
                      className="brdr-8 placeholder-img-style"
                      src={noImage}
                    />
                  </div>
                </div>
              </div>
            )}
            {photos.length === 1 && (
              <div className="banner-row">
                <div className="banner-column-full w-100 h-166">
                  <div className="full-col-img-wrap h-100">
                    <img className="brdr-8 col-img-style" src={firstPhoto} />
                  </div>
                </div>
              </div>
            )}
            {photos.length === 2 && (
              <div className="banner-row">
                <div className="inventory-banner-small banner-column-half-small h-166">
                  <div className="half-col-img-wrap h-100">
                    <img
                      className="brdr-top-left-8 brdr-btm-left-8 brdr-t-r-8 no-brdr-b-l-0 col-img-style"
                      src={firstPhoto}
                    />
                  </div>
                </div>

                <div className="inventory-banner-small banner-column-half-small h-166">
                  <div className="half-col-img-wrap-right h-100">
                    <img
                      className="brdr-top-right-8 brdr-btm-right-8 no-brdr-t-r-0 brdr-b-r-8 brdr-b-l-8 col-img-style "
                      src={secondPhoto}
                    />
                  </div>
                </div>
              </div>
            )}
            {photos.length >= 3 && (
              <div className="banner-row">
                <div className="banner-column-half h-166">
                  <div className="half-col-img-wrap h-100">
                    <img
                      className="brdr-top-left-8 brdr-btm-left-8 brdr-t-r-8 no-brder-b-l-0 col-img-style"
                      src={firstPhoto}
                    />
                  </div>
                </div>

                <div className="banner-column-quarter h-166">
                  <div className="quarter-col-img-wrap-top h-50-percent">
                    <img
                      className="brdr-top-right-8 no-brdr-t-r-0 col-img-style"
                      src={secondPhoto}
                    />
                  </div>
                  <div className="quarter-col-img-wrap-btm h-50-percent">
                    <img
                      className="brdr-btm-right-8 brdr-b-l-8 col-img-style"
                      src={thirdPhoto}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="wantad-listing-details">
            <div className="wantad-listing-details-top d-flex justify-content-between">
              <div>
                <div className="want-ad-tag mb-2 grey-tag-bg">
                  <span className="tag-icon grey-color-icon">
                    {!isSold ? "Open To " + titleText : "Sold"}
                  </span>
                </div>
                <div className="want-ad-tag mb-2 grey-tag-bg">
                  <span className="tag-icon grey-color-icon text-capitalize">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.30278 1.75224C8.00974 1.76581 7.7178 1.84107 7.44544 1.97726C7.43752 1.9812 7.42974 1.98538 7.42208 1.98979L6.59493 2.46773C6.5862 2.46728 6.57746 2.46709 6.56873 2.46716H1.02706C0.969085 2.46635 0.911527 2.47706 0.857728 2.49867C0.803929 2.52029 0.754964 2.55239 0.713677 2.59309C0.67239 2.6338 0.639606 2.68231 0.617229 2.7358C0.594852 2.78928 0.583328 2.84669 0.583328 2.90466C0.583328 2.96264 0.594852 3.02005 0.617229 3.07353C0.639606 3.12702 0.67239 3.17553 0.713677 3.21624C0.754964 3.25694 0.803929 3.28904 0.857728 3.31066C0.911527 3.33227 0.969085 3.34298 1.02706 3.34217H4.7498L4.55953 3.42135C4.55008 3.4252 4.54077 3.42938 4.53162 3.43388C4.04676 3.67631 3.82308 4.19274 3.87138 4.65751C3.91968 5.12229 4.25241 5.58564 4.78569 5.71879C4.99356 5.77059 5.22447 5.80082 5.47612 5.80082C6.75175 5.80082 8.00174 5.40334 8.3717 5.28072C8.61498 5.55233 9.81473 6.89309 10.2157 7.30359C10.5061 7.60071 10.5355 7.89079 10.4607 8.09713C10.3858 8.30347 10.216 8.46286 9.84599 8.46399C9.7455 8.46429 9.64818 8.49916 9.57038 8.56275C9.49257 8.62634 9.43903 8.71478 9.41874 8.81319C9.30966 9.34076 9.11715 9.41564 8.92086 9.46716C8.72456 9.51869 8.5352 9.474 8.5352 9.474C8.4714 9.45658 8.40448 9.45385 8.33947 9.46602C8.27446 9.47818 8.21306 9.50493 8.15987 9.54424C8.10669 9.58355 8.06311 9.63441 8.03241 9.69299C8.00171 9.75157 7.98469 9.81634 7.98263 9.88245C7.98263 9.88245 7.9681 10.1083 7.85673 10.3017C7.74536 10.4951 7.62938 10.6581 7.1099 10.6014C6.99965 10.5892 6.88891 10.6194 6.80008 10.6858C6.71125 10.7523 6.65095 10.8499 6.63139 10.9591C6.57185 11.2921 6.39078 11.4366 6.11926 11.4866C5.84775 11.5366 5.47903 11.4477 5.15654 11.1289C4.74362 10.7208 3.23802 9.18534 2.5566 8.48963C2.25489 8.18151 1.8417 8.00883 1.41158 8.00883H1.02706C0.969085 8.00801 0.911527 8.01872 0.857728 8.04034C0.803929 8.06196 0.754964 8.09405 0.713677 8.13476C0.67239 8.17547 0.639606 8.22397 0.617229 8.27746C0.594852 8.33095 0.583328 8.38835 0.583328 8.44633C0.583328 8.50431 0.594852 8.56171 0.617229 8.6152C0.639606 8.66869 0.67239 8.71719 0.713677 8.7579C0.754964 8.79861 0.803929 8.8307 0.857728 8.85232C0.911527 8.87394 0.969085 8.88465 1.02706 8.88383H1.41158C1.60913 8.88383 1.79414 8.96155 1.93168 9.10201C2.61351 9.79813 4.10489 11.3196 4.5413 11.7509C5.04452 12.2485 5.6934 12.455 6.27763 12.3474C6.75135 12.2601 7.16624 11.9298 7.37366 11.4587C7.87258 11.4171 8.25593 11.1879 8.48735 10.9147C8.49323 10.915 8.49912 10.9152 8.50501 10.9152C8.50501 10.9152 9.82157 10.9382 10.5541 10.9152C11.0637 10.8994 11.5774 10.7265 12.0306 10.3291C12.2351 10.1499 12.4811 10.0499 12.7313 10.0499H12.9854C13.0434 10.0507 13.1009 10.04 13.1547 10.0184C13.2085 9.9968 13.2575 9.96471 13.2988 9.924C13.3401 9.88329 13.3728 9.83478 13.3952 9.7813C13.4176 9.72781 13.4291 9.67041 13.4291 9.61243C13.4291 9.55445 13.4176 9.49705 13.3952 9.44356C13.3728 9.39007 13.3401 9.34156 13.2988 9.30086C13.2575 9.26015 13.2085 9.22806 13.1547 9.20644C13.1009 9.18482 13.0434 9.17411 12.9854 9.17493H12.7313C12.2489 9.17493 11.8009 9.36726 11.4541 9.6711C11.1357 9.95035 10.8828 10.0298 10.5273 10.0408H10.5267C10.2879 10.0483 9.95518 10.0468 9.64205 10.0465C9.86523 9.86253 10.0654 9.59509 10.1889 9.23759C10.6944 9.12203 11.1228 8.83819 11.2832 8.39563C11.4813 7.8494 11.3272 7.18916 10.8412 6.69177C10.4729 6.31472 8.83769 4.49288 8.83769 4.49288C8.78032 4.42887 8.70538 4.38317 8.6222 4.36149C8.53903 4.33981 8.45131 4.34311 8.37 4.37097C8.37 4.37097 6.70378 4.92582 5.47612 4.92582C5.29434 4.92582 5.13622 4.90454 4.9976 4.87C4.823 4.8264 4.75704 4.71335 4.74182 4.56694C4.72669 4.42136 4.7746 4.29283 4.92127 4.2183L6.29073 3.64978C6.30836 3.64237 6.32549 3.6338 6.342 3.62415L7.83907 2.75883C8.16986 2.59417 8.52132 2.58177 8.83997 2.70927L10.8116 3.49768C11.0356 3.58769 11.2749 3.63319 11.5157 3.63326L12.9854 3.63383C13.0434 3.63465 13.1009 3.62394 13.1547 3.60232C13.2085 3.5807 13.2575 3.54861 13.2988 3.5079C13.3401 3.46719 13.3728 3.41869 13.3952 3.3652C13.4176 3.31171 13.4291 3.25431 13.4291 3.19633C13.4291 3.13835 13.4176 3.08095 13.3952 3.02746C13.3728 2.97398 13.3401 2.92547 13.2988 2.88476C13.2575 2.84405 13.2085 2.81196 13.1547 2.79034C13.1009 2.76872 13.0434 2.75801 12.9854 2.75883L11.5162 2.75826C11.3862 2.75826 11.2579 2.73409 11.138 2.68591L11.1368 2.68535L9.16524 1.89693C8.88976 1.7867 8.59581 1.73867 8.30278 1.75224Z"
                        fill="#27AE60"
                      />
                    </svg>
                    0
                  </span>
                </div>

                <h2> {truncate(_.get(inventory, "Location", ""), 24)}</h2>
              </div>

              <div
                className="side-nav-wrap"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Dropdown>
                  <Dropdown.Toggle id="dropdown-basic" variant="" align="end">
                    <img src={WantAdMoreIcon} />
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="dpdown-menu-style" align={"end"}>
                    {showMarkAsSoldBtn && (
                      <Dropdown.Item onClick={() => onMarkAsSold()}>
                        Mark as sold
                      </Dropdown.Item>
                    )}
                    {!isSold && (
                      <Dropdown.Item
                        onClick={() => onEdit(inventoryId, isDraftItem)}
                      >
                        Edit
                      </Dropdown.Item>
                    )}

                    <Dropdown.Item
                      onClick={() => onInventoryDelete(inventoryId)}
                    >
                      Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            <ul className="wantad-btm-list mt-2">
              {bedRoom ? (
                <li>
                  <div className="list-icns">
                    <img src={BedIcon} />
                  </div>
                  <span className="list-txt">{bedRoom}</span>
                </li>
              ) : (
                <></>
              )}

              {bathRoom ? (
                <li>
                  <div className="list-icns">
                    <img src={BathIcon} />
                  </div>
                  <span className="list-txt">{bathRoom}</span>
                </li>
              ) : (
                <></>
              )}
              {sqFt ? (
                <li>
                  <div className="list-icns">
                    <img src={SqftIcon} />
                  </div>
                  <span className="list-txt">{sqFt} sqft</span>
                </li>
              ) : (
                <></>
              )}

              {acreage ? (
                <li>
                  <div className="list-icns">
                    <img src={SqftIcon} />
                  </div>
                  <span className="list-txt">{acreage + lotSizeUnit}</span>
                </li>
              ) : (
                <></>
              )}
            </ul>
          </div>
        </div>
      </a>
      <MarkAsSoldConfirmModal
        show={markAsSoldModalShow}
        onHide={handleMarkAsSoldModal}
        handleInventoryMarkAsSold={() => {
          GitchaHelpFeedbackModalShow();
          handleMarkAsSoldModal(false);
        }}
        inventoryId={inventoryId}
      />
      <HelpFeedbackModal
        show={showGitchaHelpFeedbackModal}
        onHide={GitchaHelpFeedbackModalClose}
        handleInventoryMarkAsSold={handleInventoryMarkAsSold}
        inventoryId={inventoryId}
      />
    </div>
  );
};
export default ViewClientInventory;
